import { AccountClient } from '@frontend/account/api';
import { Account } from '@frontend/account/types';
import { AuthenticationManager, getTokenActiveAccountId } from '@frontend/authentication-v2';
import { SelectInputV2 } from '@frontend/basic-forms';
import { TELLOPORT_AVAILABLE_LANGUAGES_COOKIE, TELLOPORT_SELECTED_ACCOUNT_COOKIE, getCookie, removeCookie, setCookie } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { ToastUtil } from '@frontend/toast-utils';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { LoadingScreen } from '../loading-screen/loading-screen';

const EVENT_TYPE = `cookie:${TELLOPORT_SELECTED_ACCOUNT_COOKIE}`;
const AVAILABLE_LANGUAGES_EVENT = `cookie:${TELLOPORT_AVAILABLE_LANGUAGES_COOKIE}`;

interface AccountStore {
    availableAccountsInfo: Account[];
    availableAccounts: string[];
    lastUpdate: number;
    cookieAccount: string | null;
    tokenAccount: string | null;
    currentAccount: string | null;
}

type UpdateCurrentAccount = (account: string | null) => Promise<void>;

const AccountStoreContext = createContext<{
    store: AccountStore;
    currentAccount: string;
    updateCurrentAccount: UpdateCurrentAccount;
} | null>(null);

export const AccountStoreProvider = (props: { children: React.ReactNode; noAccountsMessage?: React.ReactNode }) => {
    const [store, changeStore] = useState<AccountStore>({
        availableAccountsInfo: [],
        availableAccounts: [],
        lastUpdate: 0,
        cookieAccount: getCookie(TELLOPORT_SELECTED_ACCOUNT_COOKIE) ?? null,
        tokenAccount: null,
        currentAccount: null
    });
    const storeRef = useRef(store);
    const location = useLocation();
    const isStandalonePath = location.pathname.includes('/standalone');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isResolvingAccount, setIsResolvingAccount] = useState<boolean>(false);
    const [fetchError, setFetchError] = useState<boolean>(false);

    const listener = () => {
        changeStore((prev) => ({ ...prev, cookieAccount: getCookie(TELLOPORT_SELECTED_ACCOUNT_COOKIE) ?? null }));
    };

    useEffect(() => {
        if (isStandalonePath && !AuthenticationManager.getInstance().hasValidToken()) {
            setIsLoading(false);
            return;
        }

        AuthenticationManager.getInstance()
            .waitForToken()
            .then((res) => {
                changeStore((prev) => ({ ...prev, tokenAccount: getTokenActiveAccountId(res) }));
            });
        AccountClient.fetchAccounts({ size: '100', sort_field: 'name', sort_direction: 'asc' })
            .then((res) => {
                changeStore((prev) => ({
                    ...prev,
                    availableAccountsInfo: res.results,
                    availableAccounts: res.results.map((account) => account.id)
                }));
                setIsLoading(false);
            })
            .catch(() => {
                if (window.location.host.includes('terminal') || window.location.host.includes('localhost:8')) {
                    setTimeout(() => {
                        window.location.reload();
                    }, 1000 * 10);
                } else {
                    setFetchError(true);
                    setIsLoading(false);
                }
            });

        window.addEventListener(EVENT_TYPE, listener);
        return () => window.removeEventListener(EVENT_TYPE, listener);
    }, []);

    const updateCurrentAccount = useCallback<UpdateCurrentAccount>(async (account) => {
        if (!account) {
            return;
        }

        const auth = AuthenticationManager.getInstance();
        const token = await auth.waitForToken();
        let tokenAccountId = getTokenActiveAccountId(token);

        if (tokenAccountId !== account) {
            try {
                const newToken = await auth.switchAccount(account);
                tokenAccountId = getTokenActiveAccountId(newToken);
            } catch (error) {
                ToastUtil.error('Unable to switch account. Please try again.');
                throw error;
            }
        }

        changeStore((prev) => ({
            ...prev,
            currentAccount: account,
            tokenAccount: tokenAccountId,
            cookieAccount: account
        }));
        setCookie(TELLOPORT_SELECTED_ACCOUNT_COOKIE, account);
        window.dispatchEvent(new CustomEvent(EVENT_TYPE, { detail: {} }));
    }, []);

    useEffect(() => {
        if (store.currentAccount || !store.tokenAccount || store.availableAccounts.length === 0) {
            return;
        }

        const preferredAccount = store.cookieAccount && store.availableAccounts.includes(store.cookieAccount) ? store.cookieAccount : store.tokenAccount;

        let cancelled = false;
        setIsResolvingAccount(true);

        (async () => {
            try {
                if (!cancelled) {
                    await updateCurrentAccount(preferredAccount);
                }
            } catch {
                if (!cancelled && preferredAccount !== store.tokenAccount) {
                    try {
                        await updateCurrentAccount(store.tokenAccount);
                    } catch {
                        // Leave currentAccount unset; SelectAccount / error UI can surface.
                    }
                }
            } finally {
                if (!cancelled) {
                    setIsResolvingAccount(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [store.availableAccounts, store.cookieAccount, store.tokenAccount, store.currentAccount, updateCurrentAccount]);

    useEffect(() => {
        if (store.currentAccount) {
            window.dispatchEvent(new CustomEvent(EVENT_TYPE, { detail: {} }));
        }
    }, [location]);

    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    useEffect(() => {
        if (!store.currentAccount) return;
        SettingClient.fetchSettings({
            key: SettingKey.AVAILABLE_LANGUAGES,
            entity_type: 'account',
            entity_id: store.currentAccount
        })
            .then((res) => {
                const available = res.results.find((s) => s.key === SettingKey.AVAILABLE_LANGUAGES);
                if (available?.value && Array.isArray(available.value) && (available.value as string[]).length > 0) {
                    setCookie(TELLOPORT_AVAILABLE_LANGUAGES_COOKIE, JSON.stringify(available.value));
                } else {
                    removeCookie(TELLOPORT_AVAILABLE_LANGUAGES_COOKIE);
                }
            })
            .catch(() => {
                removeCookie(TELLOPORT_AVAILABLE_LANGUAGES_COOKIE);
            })
            .finally(() => {
                window.dispatchEvent(new CustomEvent(AVAILABLE_LANGUAGES_EVENT));
            });
    }, [store.currentAccount]);

    if (fetchError) {
        return (
            <div className='flex min-h-screen min-w-screen flex-col items-center justify-center gap-6 bg-white p-8'>
                <div className='flex flex-col items-center gap-2 text-center'>
                    <p className='text-lg font-semibold text-gray-800'>
                        <FormattedMessage
                            id='account-repository.fetch-error.title'
                            defaultMessage='Unable to load your account'
                            description='Error title shown when account fetch fails'
                        />
                    </p>
                    <p className='text-sm text-gray-500'>
                        <FormattedMessage
                            id='account-repository.fetch-error.description'
                            defaultMessage='Your account does not have access to any workspace. Please contact your administrator to get access assigned.'
                            description='Error description shown when account fetch fails'
                        />
                    </p>
                </div>
                <div className='flex w-full max-w-xs flex-col gap-3'>
                    <button
                        className='w-full rounded-md border border-gray-800 bg-gray-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-700 active:bg-gray-900'
                        onClick={() => window.location.reload()}>
                        {CommonMessage.BUTTONS.REFRESH}
                    </button>
                    <button
                        className='w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                        onClick={() => AuthenticationManager.getInstance().doLogout()}>
                        {CommonMessage.BUTTONS.LOGOUT}
                    </button>
                </div>
            </div>
        );
    }

    if (store.currentAccount === null) {
        if (isStandalonePath && !AuthenticationManager.getInstance().hasValidToken()) {
            return (
                <AccountStoreContext.Provider
                    value={{
                        store,
                        currentAccount: '',
                        updateCurrentAccount
                    }}>
                    {props.children}
                </AccountStoreContext.Provider>
            );
        }
        if (isLoading || isResolvingAccount) {
            return <LoadingScreen />;
        }
        if (store.availableAccounts.length === 0 && props.noAccountsMessage) {
            return props.noAccountsMessage;
        }
        return (
            <SelectAccount
                selectAccount={updateCurrentAccount}
                availableAccounts={store.availableAccountsInfo}
            />
        );
    }
    return (
        <AccountStoreContext.Provider value={{ store, currentAccount: store.currentAccount, updateCurrentAccount }}>
            {props.children}
        </AccountStoreContext.Provider>
    );
};

export const useAccountRepository = () => {
    const context = useContext(AccountStoreContext);
    if (!context) {
        throw new Error('useAccountRepository must be used within an AccountStoreProvider');
    }
    return context;
};

const SelectAccount = (props: { availableAccounts: Account[]; selectAccount: UpdateCurrentAccount }) => {
    const [selected, setSelected] = useState<Account | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!selected || isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            await props.selectAccount(selected.id);
        } catch {
            // Toast already shown by updateCurrentAccount
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='flex h-screen w-screen flex-col'>
            <form
                className='mx-auto my-40 h-1/2 w-full rounded-lg bg-white sm:w-1/2 sm:shadow-lg'
                onSubmit={onSubmit}>
                <div className='flex h-1/2 w-full flex-col items-center justify-between p-4'>
                    <h1 className='text-primary m-au to text-2xl font-bold'>Select an account</h1>
                    <SelectInputV2<Account>
                        className='w-full'
                        options={props.availableAccounts.map((acc) => ({
                            value: acc.id,
                            label: acc.name,
                            metadata: acc
                        }))}
                        onSelect={(v) => setSelected(v?.metadata || null)}
                        value={selected?.id}
                    />
                </div>
                <div className='flex h-1/2 w-full flex-col items-center justify-end gap-3 sm:justify-center'>
                    <button
                        id='select-account-submit'
                        type='submit'
                        data-testid='select-account-submit'
                        className='btn bg-primary w-1/3'
                        disabled={selected === null || isSubmitting}>
                        <div className='justify-start text-base font-medium text-white'>{CommonMessage.BUTTONS.SUBMIT}</div>
                    </button>
                </div>
            </form>
        </div>
    );
};
